import React, { useEffect, useMemo, useCallback } from 'react';
import { useUnityBuildStore } from '../../stores/store';
import { useKeyboardEvents } from '../../eventManager/keyboardEvents/useKeyboardEvents';
import { SVGPointsNew as SVGPoints } from './SVGPointsNew';
import { useMouseEvents } from '../../eventManager/mouseEvents/useMouseEvents';
import { SVGMeasurement } from './SVGMeasurement';
import { SVGCrosshair } from './SVGCrosshair';
import { SVGCursorPreview } from './SVGCursorPreview';
import { SVGPointLineNew as SVGPointLine } from './SVGPointLineNew';

// import { DragPreview } from './DragPreview'; // Removed - using live positions directly
import { useStore } from 'zustand';
import { SVGInferenceBoxes } from './SVGInferenceBoxes';
import { useKeyBoardEventStore } from '../../stores/keyEventStore';
import { useSelectionStore } from '../../stores/SelectionStore';
import { useAssemblyStore } from '../../stores/AssemblyStore';
import { SVGAnnotations } from '../annotations/SVGAnnotations';
import { useAnnotationStore } from '../annotations/annotationStore';
import { AreaDrawingTool } from './AreaDrawingTool';
import { AreaDrawingEnhancements } from './AreaDrawingEnhancements';
import { AreaPolygonRenderer } from './AreaPolygonRenderer';

export const TakeoffSVG: React.FC = () => {
  const svgRef = React.useRef<SVGSVGElement>(null);
  const currentTakeoffAssembly = useStore(useAssemblyStore).currentTakeoffAssembly;
  useKeyboardEvents({ svgRef });

  const store = useStore(useUnityBuildStore);
  const { isLassoMode, isLassoDrawing, lassoPoints } = useStore(useUnityBuildStore);
  const { selectedPoints, clearCopiedItems } = useStore(useSelectionStore);
  const { isAnnotating, activeAnnotationType } = useStore(useAnnotationStore);

  const { enableCrosshair } = useStore(useKeyBoardEventStore);

  const {
    handleTakeoffButton0MouseEvents,
    handleTakeoffButton2MouseEvents,
    handleMouseMove,
    handleMouseUp,
    cursorPositionRef,
    cursorPosition,
    isDragSelecting,
    dragStartPosition,
    renderPastePreview,
    clearPastePreview,
    selectedPoints: hookSelectedPoints,
  } = useMouseEvents(svgRef);

  // Use hook-provided selected points if available, otherwise use the store's
  const effectiveSelectedPoints = useMemo(
    () => (hookSelectedPoints?.length ? hookSelectedPoints : selectedPoints),
    [hookSelectedPoints, selectedPoints]
  );

  // Consolidate mouse handlers to avoid re-creating them on each render
  const handleMouseButton = useCallback(
    (e: React.MouseEvent) => {
      if (e.button === 0) {
        handleTakeoffButton0MouseEvents(e);
      } else if (e.button === 2) {
        e.preventDefault();
        handleTakeoffButton2MouseEvents(e);
      }
    },
    [handleTakeoffButton0MouseEvents, handleTakeoffButton2MouseEvents]
  );

  // Gate event manager when annotation shapes are active to avoid conflicts
  const handleSvgMouseDown = useCallback(
    (e: React.MouseEvent) => {
      if (isAnnotating && activeAnnotationType && activeAnnotationType !== 'text') {
        // Let AnnotationInteractionHandler manage this
        return;
      }

      // Clear shape selection when clicking on empty area
      const target = e.target as SVGElement;
      if (target.id === 'svgCanvas' || target.tagName === 'svg') {
        const annotationStore = useAnnotationStore.getState();
        if (annotationStore.selectedShapeId) {
          annotationStore.setSelectedShapeId(null);
          console.log('Shape selection cleared by clicking outside');
        }
      }

      handleMouseButton(e);
    },
    [isAnnotating, activeAnnotationType, handleMouseButton]
  );

  const handleSvgMouseMove = useCallback(
    (e: React.MouseEvent) => {
      // Always update global cursor position so annotation tools have accurate coords
      handleMouseMove(e);
    },
    [handleMouseMove]
  );

  const handleSvgMouseUp = useCallback(
    (e: React.MouseEvent) => {
      if (isAnnotating && activeAnnotationType && activeAnnotationType !== 'text') {
        // Let AnnotationInteractionHandler manage this
        return;
      }
      handleMouseUp();
    },
    [isAnnotating, activeAnnotationType, handleMouseUp]
  );

  // Clean up any listeners on unmount
  useEffect(() => {
    return () => {
      // Clear any leftover state or listeners
      clearPastePreview();
      clearCopiedItems();
    };
  }, [clearPastePreview, clearCopiedItems]);

  // Render selection rectangle while dragging
  const renderSelectionRectangle = useCallback(() => {
    if (!isDragSelecting || isLassoMode) return null;

    const x = Math.min(dragStartPosition.x, cursorPosition.x);
    const y = Math.min(dragStartPosition.y, cursorPosition.y);
    const width = Math.abs(dragStartPosition.x - cursorPosition.x);
    const height = Math.abs(dragStartPosition.y - cursorPosition.y);

    return (
      <rect
        x={x}
        y={y}
        width={width}
        height={height}
        fill="rgba(66, 133, 244, 0.2)"
        stroke="#4285F4"
        strokeWidth={1}
        strokeDasharray="5,5"
      />
    );
  }, [isDragSelecting, dragStartPosition, cursorPosition, isLassoMode]);

  // Render free-form lasso path while drawing
  const renderLassoPath = useCallback(() => {
    if (!isLassoMode || lassoPoints.length === 0) return null;

    const pathData = lassoPoints
      .map((p, idx) => (idx === 0 ? `M ${p.x} ${p.y}` : `L ${p.x} ${p.y}`))
      .join(' ');

    return (
      <path
        d={pathData}
        fill="none"
        stroke="#FF9800"
        strokeWidth={2}
        strokeDasharray={isLassoDrawing ? undefined : '4,2'}
        opacity={0.9}
      />
    );
  }, [isLassoMode, isLassoDrawing, lassoPoints]);

  // Render selection indicators around selected points
  const renderSelectionIndicators = useCallback(() => {
    if (!effectiveSelectedPoints || effectiveSelectedPoints.length === 0) return null;

    return effectiveSelectedPoints.map(point => (
      <rect
        key={`selection-${point.pointId}`}
        x={point.originalX - 8}
        y={point.originalY - 8}
        width={16}
        height={16}
        fill="none"
      />
    ));
  }, [effectiveSelectedPoints]);

  // Render the paste preview
  const renderPastePreviewElements = useCallback(() => {
    const preview = renderPastePreview?.();
    if (!preview) return null;

    return preview.map(
      (item: {
        key: string;
        type: 'circle' | 'rect' | 'line' | 'polygon' | 'group';
        x?: number;
        y?: number;
        radius?: number;
        width?: number;
        height?: number;
        x1?: number;
        y1?: number;
        x2?: number;
        y2?: number;
        points?: string;
        children?: Array<{
          type: 'line';
          x1: number;
          y1: number;
          x2: number;
          y2: number;
          stroke: string;
          strokeWidth: number;
        }>;
        fill?: string;
        stroke?: string;
        strokeWidth?: number;
        strokeDasharray?: string;
        strokeOpacity?: number;
        isPreview: boolean;
        isPasteFrozen: boolean;
        pointType?: string;
      }) => {
        // Render different element types based on the item type
        switch (item.type) {
          case 'circle':
            return (
              <circle
                key={item.key}
                cx={item.x}
                cy={item.y}
                r={item.radius}
                fill={item.fill}
                stroke={item.stroke}
                strokeWidth={item.strokeWidth}
                fillOpacity={0.7} // Make preview slightly transparent
                // strokeDasharray={item.strokeDasharray} <--- Dont render this, it looks bad
              />
            );
          case 'rect':
            return (
              <rect
                key={item.key}
                x={item.x}
                y={item.y}
                width={item.width}
                height={item.height}
                fill={item.fill}
                stroke={item.stroke}
                strokeWidth={item.strokeWidth}
                fillOpacity={0.7} // Make preview slightly transparent
              />
            );
          case 'line':
            return (
              <line
                key={item.key}
                x1={item.x1}
                y1={item.y1}
                x2={item.x2}
                y2={item.y2}
                stroke={item.stroke}
                strokeWidth={item.strokeWidth}
                strokeDasharray={item.strokeDasharray}
                strokeOpacity={item.strokeOpacity || 0.7} // Make preview slightly transparent
              />
            );
          case 'polygon':
            return (
              <polygon
                key={item.key}
                points={item.points}
                fill={item.fill}
                stroke={item.stroke}
                strokeWidth={item.strokeWidth}
                fillOpacity={0.7} // Make preview slightly transparent
              />
            );
          case 'group':
            return (
              <g key={item.key}>
                {item.children?.map((child, index) => (
                  <line
                    key={`${item.key}-line-${index}`}
                    x1={child.x1}
                    y1={child.y1}
                    x2={child.x2}
                    y2={child.y2}
                    stroke={child.stroke}
                    strokeWidth={child.strokeWidth}
                    strokeOpacity={0.7} // Make preview slightly transparent
                  />
                ))}
              </g>
            );
          default:
            return null;
        }
      }
    );
  }, [renderPastePreview]);

  // Memoize the SVG content based on whether a takeoff assembly is current
  const takeoffSvgContent = useMemo(() => {
    if (currentTakeoffAssembly) {
      return (
        <>
          {/* When assembly is active, render SVG elements in this order */}
          <AreaPolygonRenderer />
          <SVGPointLine cursorPositionRef={cursorPositionRef} />
          <AreaDrawingTool cursorPositionRef={cursorPositionRef} />
          <AreaDrawingEnhancements cursorPositionRef={cursorPositionRef} />
          <SVGPoints
            cursorPositionRef={cursorPositionRef}
            selectedPoints={effectiveSelectedPoints}
          />
          {/* <DragPreview /> - Removed, using live positions directly */}
          {renderPastePreviewElements()}
          {renderLassoPath()}
          {renderSelectionIndicators()}
          {store.selectedObject && <SVGInferenceBoxes />}
          {/* Render measurements as view-only when working with assembly */}
          <SVGMeasurement cursorPositionRef={cursorPositionRef} isViewOnlyMode={true} />
          {/* Notes are now rendered separately outside this memo for better stability */}
        </>
      );
    } else {
      return (
        <>
          {/* When no assembly is active, render in this order */}
          <AreaPolygonRenderer />
          <SVGPointLine cursorPositionRef={cursorPositionRef} />
          <AreaDrawingTool cursorPositionRef={cursorPositionRef} />
          <AreaDrawingEnhancements cursorPositionRef={cursorPositionRef} />
          <SVGPoints
            cursorPositionRef={cursorPositionRef}
            selectedPoints={effectiveSelectedPoints}
          />
          {/* <DragPreview /> - Removed, using live positions directly */}
          {renderPastePreviewElements()}
          {renderSelectionRectangle()}
          {renderLassoPath()}
          {renderSelectionIndicators()}
          {store.selectedObject && <SVGInferenceBoxes />}
          {/* Fully interactive measurements when no assembly is active */}
          <SVGMeasurement cursorPositionRef={cursorPositionRef} isViewOnlyMode={false} />
          {/* Notes are now rendered separately outside this memo for better stability */}
        </>
      );
    }
  }, [
    currentTakeoffAssembly,
    cursorPositionRef,
    effectiveSelectedPoints,
    renderPastePreviewElements,
    renderSelectionIndicators,
    store.selectedObject,
    renderSelectionRectangle,
    renderLassoPath,
  ]);

  return (
    <>
      <svg
        ref={svgRef}
        tabIndex={0}
        id="svgCanvas"
        onMouseDown={handleSvgMouseDown}
        onMouseMove={handleSvgMouseMove}
        onMouseUp={handleSvgMouseUp}
        onContextMenu={event => event.preventDefault()}
        style={{
          position: 'absolute',
          width: `${store.pdfDimensions.width}px`,
          height: `${store.pdfDimensions.height}px`,
          pointerEvents: 'auto',
          zIndex: 2,
          cursor: isAnnotating
            ? activeAnnotationType === 'text'
              ? 'text'
              : 'crosshair'
            : store.isInsertingPoint
              ? 'none'
              : 'crosshair',
        }}
        xmlns="http://www.w3.org/2000/svg"
        xmlnsXlink="http://www.w3.org/1999/xlink"
      >
        {/* Cursor preview for current assembly shape */}
        <SVGCursorPreview cursorPosition={cursorPosition} />

        {/* Custom bullseye cursor for point insertion mode */}
        {store.isInsertingPoint && (
          <g>
            {/* Outer circle with pulsing effect */}n
            <circle
              cx={cursorPosition.x}
              cy={cursorPosition.y}
              r={12}
              fill="none"
              stroke="#4285F4"
              strokeWidth={2}
              opacity={0.8}
              style={{
                filter: 'drop-shadow(0 0 4px rgba(66, 133, 244, 0.6))',
              }}
            />
            {/* Inner circle */}
            <circle
              cx={cursorPosition.x}
              cy={cursorPosition.y}
              r={6}
              fill="none"
              stroke="#4285F4"
              strokeWidth={2}
              opacity={0.8}
            />
            {/* Center dot */}
            <circle
              cx={cursorPosition.x}
              cy={cursorPosition.y}
              r={2}
              fill="#4285F4"
              opacity={0.9}
            />
            {/* Crosshair lines */}
            <line
              x1={cursorPosition.x - 16}
              y1={cursorPosition.y}
              x2={cursorPosition.x - 8}
              y2={cursorPosition.y}
              stroke="#4285F4"
              strokeWidth={1.5}
              opacity={0.6}
            />
            <line
              x1={cursorPosition.x + 8}
              y1={cursorPosition.y}
              x2={cursorPosition.x + 16}
              y2={cursorPosition.y}
              stroke="#4285F4"
              opacity={0.6}
              strokeWidth={1.5}
            />
            <line
              x1={cursorPosition.x}
              y1={cursorPosition.y - 16}
              x2={cursorPosition.x}
              y2={cursorPosition.y - 8}
              stroke="#4285F4"
              strokeWidth={1.5}
              opacity={0.6}
            />
            <line
              x1={cursorPosition.x}
              y1={cursorPosition.y + 8}
              x2={cursorPosition.x}
              y2={cursorPosition.y + 16}
              stroke="#4285F4"
              strokeWidth={1.5}
              opacity={0.6}
            />
          </g>
        )}
        {/* DO NOT REORGANIZE THE ORDER OF THESE ELEMENTS. TAKEOFF ANNOTATIONS TAKE PRIORITY.
        Render annotations before takeoff assembly content "takeoffSvgContent" 
        we want takeoff assembly content to be on top of annotations */}
        <SVGAnnotations />

        {enableCrosshair && <SVGCrosshair cursorPosition={cursorPosition} />}

        {/* Main SVG content (takeoff annotations take priority and should be on top) */}
        {takeoffSvgContent}
      </svg>
    </>
  );
};
